import { Button, Modal, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { useCreatePlaceholderMember } from 'src/Hooks/Mutations/useCreatePlaceholderMember';

import styles from './CreatePlaceholderMemberModal.module.scss';
import { Props } from './CreatePlaceholderMemberModal.types';

interface FormValues {
  firstName: string;
  lastName: string;
  estimatedEmail: string;
}

const CreatePlaceholderMemberModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const { mutate: createPlaceholder, isLoading } = useCreatePlaceholderMember();

  const form = useForm<FormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      estimatedEmail: '',
    },
    validate: {
      firstName: value => (value.trim().length === 0 ? 'Le prénom est requis' : null),
      lastName: value => (value.trim().length === 0 ? 'Le nom est requis' : null),
      estimatedEmail: value =>
        value.trim().length === 0
          ? "L'email est requis"
          : !/^\S+@\S+\.\S+$/.test(value)
            ? 'Email invalide'
            : null,
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = form.onSubmit(values => {
    createPlaceholder(
      {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        estimatedEmail: values.estimatedEmail.trim(),
      },
      { onSuccess: handleClose }
    );
  });

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      title={t('admin.placeholderMembers.create.title', 'Ajouter un membre')}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <div className={styles.container}>
          <p className={styles.subtitle}>
            {t(
              'admin.placeholderMembers.create.description',
              'Il ou elle pourra rejoindre ShareHeritage à tout moment en créant un compte avec cet email. En attendant, vous pouvez déjà tout configurer : rôle, parts, foyer, participation aux projets et dépenses.'
            )}
          </p>
          <TextInput
            label={t('admin.placeholderMembers.create.firstName', 'Prénom')}
            withAsterisk
            {...form.getInputProps('firstName')}
          />
          <TextInput
            label={t('admin.placeholderMembers.create.lastName', 'Nom')}
            withAsterisk
            {...form.getInputProps('lastName')}
          />
          <TextInput
            label={t('admin.placeholderMembers.create.estimatedEmail', 'Email')}
            withAsterisk
            placeholder="prenom@exemple.com"
            {...form.getInputProps('estimatedEmail')}
          />

          <div className={styles.buttons}>
            <Button type="submit" loading={isLoading}>
              {t('admin.placeholderMembers.create.submit', 'Ajouter')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default CreatePlaceholderMemberModal;
